import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { AgreementDto, CreateAgreementDto } from '../../common/PricePrgClient';
import { ProgressButton } from '../../common/ProgressButton';
import { scrollbar } from '../../common/theme/scrollbar';
import { RootState } from '../../store';
import { toDateString, toPriceString } from '../../utils';
import { getProducts } from '../product/redux/productSelectors';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import { getAvailableUserGroups } from '../usergroup/redux/userGroupSelectors';
import AgreementForm from './AgreementForm';
import { getAgreements } from './redux/agreementSelectors';
import { cancelCopyAgreementRequiresConfirmation, copyAgreement } from './redux/agreementSlices';
const mapStateToProps = (state: RootState) => ({
    isCreating: state.agreement.ui.isSaving,
    priceConflicts: state.agreement.ui.copyPriceConflicts,
    agreements: getAgreements(state),
    products: getProducts(state),
    userGroups: getAvailableUserGroups(state),
    isAdmin: getCurrentUserPermissions(state).isAdmin
});

type OwnProps = {
    closeDialog: () => void;
    agreementToCopy: AgreementDto;
};
type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const CopyAgreementDialog = ({
    closeDialog,
    isCreating,
    agreementToCopy,
    priceConflicts,
    agreements,
    products,
    userGroups,
    isAdmin
}: Props) => {
    const defaultAgreementValues = useMemo<CreateAgreementDto>(() => {
        let permittedUserGroupIds = agreementToCopy.permittedUserGroups;

        if (!isAdmin) {
            permittedUserGroupIds = agreementToCopy.permittedUserGroups.filter(agUg =>
                userGroups.some((ug: { userGroupId: number }) => ug.userGroupId === agUg.userGroupId)
            );
        }

        return {
            name: `Copy of ${agreementToCopy.name}`,
            description: agreementToCopy.description,
            customerConnectionType: agreementToCopy.customerConnectionType,
            currency: agreementToCopy.currency,
            validFrom: agreementToCopy.validFrom,
            validTo: agreementToCopy.validTo,
            customerId: agreementToCopy.customerId,
            customerGroupId: agreementToCopy.customerGroupId,
            agreementType: agreementToCopy.agreementType,
            handleExternalPrices: agreementToCopy.handleExternalPrices ?? false,
            permittedUserGroups: permittedUserGroupIds.map(c => c.userGroupId) ?? [],
            preorderDate: agreementToCopy.preorderDate ?? undefined
        };
    }, [userGroups, agreementToCopy, isAdmin]);

    const dispatch = useDispatch();
    const [awareOfPriceConflicts, setAwareOfPriceConflicts] = useState(false);

    const handleSubmit = (form: CreateAgreementDto) => {
        dispatch(
            copyAgreement.request({
                agreementId: agreementToCopy.agreementId,
                additionalDetails: form,
                awareOfPriceConflicts
            })
        );
    };

    const handleBack = () => {
        dispatch(cancelCopyAgreementRequiresConfirmation());
    };
    const [hasPriceConflicts, setHasPriceConflicts] = useState(false);
    useEffect(() => {
        if (priceConflicts.length > 0) {
            setHasPriceConflicts(true);
        }
    }, [priceConflicts]);
    return (
        <Dialog open={true} sx={{ minWidth: '900px' }} maxWidth={'xl'}>
            <DialogTitle>Copy Agreement</DialogTitle>
            <DialogContent
                sx={{
                    overflowX: 'hidden',
                    minWidth: '900px',
                    maxWidth: theme => theme.breakpoints.values.xl
                }}
            >
                <AgreementForm defaultValues={defaultAgreementValues} submitForm={handleSubmit} />

                {hasPriceConflicts && (
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Typography variant="subtitle2" color="error">
                                Some prices already exists on other agreements. These prices will be removed
                                in the copied agreement. Do you still want to copy the agreement?
                            </Typography>
                        </Grid>

                        <Grid item>
                            <TableContainer
                                sx={theme => ({
                                    maxHeight: '60vh',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    ...scrollbar(theme)
                                })}
                            >
                                <Table size="small" aria-label="Agreement prices conflicts" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Agreement</TableCell>
                                            <TableCell>From</TableCell>
                                            <TableCell>To</TableCell>
                                            <TableCell>Product id</TableCell>
                                            <TableCell>Product name</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {priceConflicts.map((ap, idx) => {
                                            const agreement = agreements[ap.agreementId];
                                            let agreementName = '??';
                                            if (agreement != null) {
                                                agreementName =
                                                    agreement.externalReference != null &&
                                                    agreement.externalReference.length > 0
                                                        ? `${agreement.externalReference} - ${agreement.name}`
                                                        : agreement.name;
                                            }
                                            const productName = products[ap.productId]?.name ?? '??';

                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell component="th" scope="row">
                                                        {agreementName}
                                                    </TableCell>
                                                    <TableCell>{toDateString(agreement.validFrom)}</TableCell>
                                                    <TableCell>{toDateString(agreement.validTo)}</TableCell>
                                                    <TableCell>{ap.productId}</TableCell>
                                                    <TableCell>{productName}</TableCell>
                                                    <TableCell align="right">
                                                        <Box fontWeight="fontWeightBold">
                                                            {toPriceString(ap.price)}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                {!hasPriceConflicts ? (
                    <>
                        <Button onClick={closeDialog}>Cancel</Button>
                        <ProgressButton
                            color="primary"
                            type="submit"
                            form="agreement-form"
                            isLoading={isCreating}
                        >
                            Create copy
                        </ProgressButton>
                    </>
                ) : (
                    <Grid container direction="row" justifyContent="space-between">
                        <Grid item>
                            <Button size="small" onClick={handleBack}>
                                <KeyboardArrowLeft />
                                Back
                            </Button>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row">
                                <Button onClick={closeDialog}>Cancel</Button>
                                <ProgressButton
                                    onClick={() => setAwareOfPriceConflicts(true)}
                                    color="primary"
                                    type="submit"
                                    form="agreement-form"
                                    isLoading={isCreating}
                                >
                                    Create copy
                                </ProgressButton>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(CopyAgreementDialog);
